import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    ButtonRow,
    Checkbox,
    Collapse,
    Divider,
    Form,
    FormGroup,
    FormSection,
    Link,
    Radio,
    RadioGroup,
    Select,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { isBoolean } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEmploymentTaxesDetails } from '@common/interfaces/employmentTaxesDetails';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { employmentTaxesDetailsAddEditSchema } from '@common/validation/employmentTaxesDetails';
import { IndependentDateInput } from '@frontend/components/fields';
import { getSelectedLanguage } from '@frontend/translate';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';
import { getCountryCodes, getCountryOptions } from '@frontend/utils/countriesUtils';
import { getCountryOption } from '@frontend/utils/getCountryOption';

interface IEmploymentTaxesDetailsAddEdit extends Omit<IEmploymentTaxesDetails, 'isNaturalPerson'> {
    isNaturalPerson: boolean | null;
}

const emptyEmploymentTaxesDetails = {
    birthDate: null,
    city: null,
    country: null,
    endDate: null,
    houseNumber: null,
    houseNumberSuffix: null,
    identifierSocialSecurity: null,
    isNaturalPerson: null,
    street: null,
    zip: null,
};

interface IEmploymentTaxesDetailsAddEditProps {
    employmentTaxesDetails?: IEmploymentTaxesDetailsAddEdit;
    onClose: () => void;
    onSubmit: (copyDataToAllEmployments?: boolean) => void;
}

export function EmploymentTaxesDetailsAddEdit(props: Readonly<IEmploymentTaxesDetailsAddEditProps>) {
    const abortableFetch = useAbortableFetch();
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);

    const [employmentTaxesDetails, setEmploymentTaxesDetails] = useState<IEmploymentTaxesDetailsAddEdit>(
        props.employmentTaxesDetails ?? emptyEmploymentTaxesDetails
    );

    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [showCopyDataToAllEmploymentsCheckbox, setShowCopyDataToAllEmploymentsCheckbox] = useState<boolean>(false);
    const [copyDataToAllEmployments, setCopyDataToAllEmployments] = useState<boolean>(false);
    const [collapsedTextIsOpen, setCollapsedTextIsOpen] = useState<boolean>(false);

    const language = getSelectedLanguage();

    useEffect(() => {
        loadAllActivePractitionerEmployments();
    }, []);

    return (
        <Form submitOnEnter={true}>
            <Text marginBottom={6}>{language.employmentTaxesDetails.naturalPersonQuestion}</Text>

            <FormGroup
                hasErrors={validationErrors.has('isNaturalPerson')}
                isRequired
                label={language.employmentTaxesDetails.isNaturalPersonLabel}
            >
                <RadioGroup
                    error={getValidationErrorTranslation(validationErrors.get('isNaturalPerson'))}
                    name={'isNaturalPerson'}
                    onBlur={() => validateOne('isNaturalPerson')}
                    onChange={(event) => handleChange('isNaturalPerson', event.target.value === 'true')}
                    value={
                        isBoolean(employmentTaxesDetails.isNaturalPerson)
                            ? employmentTaxesDetails.isNaturalPerson.toString()
                            : ''
                    }
                >
                    <Radio
                        label={language.common.yes}
                        value="true"
                    />

                    <Radio
                        label={language.common.no}
                        value="false"
                    />
                </RadioGroup>
            </FormGroup>

            {employmentTaxesDetails.isNaturalPerson && (
                <>
                    <Divider />

                    <FormSection title="Persoonsgegevens">
                        <Text mb={2}>
                            {language.employmentTaxesDetails.personalDataInfoFirstPart}
                            <Link
                                fontWeight="normal"
                                href="https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/zakelijk/personeel_en_loon/uitbetaalde_bedragen_aan_derden/"
                                openInNewTab
                                variant="externalLink"
                            >
                                {language.employmentTaxesDetails.personalDataInfoLink}
                            </Link>
                            {language.employmentTaxesDetails.personalDataInfoSecondPart}
                        </Text>
                        <Link
                            color="minimal"
                            fontWeight="normal"
                            iconLeft={collapsedTextIsOpen ? 'caret-up' : 'caret-down'}
                            onClick={() => setCollapsedTextIsOpen(!collapsedTextIsOpen)}
                            pb={4}
                            pt={2}
                            size="xs"
                            variant="button"
                        >
                            {collapsedTextIsOpen ? language.common.lessInformation : language.common.moreInformation}
                        </Link>
                        <Collapse isOpen={collapsedTextIsOpen}>
                            <Text
                                as="p"
                                mb={4}
                                size="sm"
                            >
                                {language.employmentTaxesDetails.alert.collapsedTextFirstPart}
                                <br />
                                {language.employmentTaxesDetails.alert.collapsedTextSecondPart}
                                <Link
                                    color="primary"
                                    fontWeight="normal"
                                    href="mailto:privacy@ksyos.nl"
                                    openInNewTab={true}
                                    variant="externalLink"
                                >
                                    privacy@ksyos.nl.
                                </Link>
                            </Text>
                        </Collapse>
                        <FormGroup
                            hasErrors={validationErrors.has('identifierSocialSecurity')}
                            isRequired
                            label={language.common.bsn}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(validationErrors.get('identifierSocialSecurity'))}
                                onBlur={() => validateOne('identifierSocialSecurity')}
                                onChange={({ target: { value } }) => handleChange('identifierSocialSecurity', value)}
                                sx={{ width: '168px' }}
                                type="text"
                                value={employmentTaxesDetails.identifierSocialSecurity || ''}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={validationErrors.has('birthDate')}
                            isRequired
                            label={language.common.birthDate}
                        >
                            <IndependentDateInput
                                error={validationErrors.get('birthDate')}
                                hasMaxWidth={true}
                                maxDate={DateTime.now().format()}
                                onChange={(value) => {
                                    handleChange('birthDate', value);
                                    validateOne('birthDate', { ...employmentTaxesDetails, birthDate: value });
                                }}
                                placeholder={language.common.datePlaceholder}
                                size="large"
                                value={employmentTaxesDetails.birthDate?.toString()}
                                variant="outline"
                            />
                        </FormGroup>
                    </FormSection>

                    <FormSection
                        isBorderless
                        title="Adres"
                    >
                        <FormGroup
                            hasErrors={validationErrors.has('street')}
                            isRequired
                            label={language.common.street}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(validationErrors.get('street'))}
                                onBlur={() => validateOne('street')}
                                onChange={({ target: { value } }) => handleChange('street', value)}
                                value={employmentTaxesDetails.street || ''}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={validationErrors.has('houseNumber') || validationErrors.has('houseNumberSuffix')}
                            htmlFor="houseNumber"
                            isRequired
                            label={language.common.houseNumber}
                        >
                            <Box
                                display={'flex'}
                                justifyContent={'space-between'}
                            >
                                <Box
                                    marginRight={2}
                                    width={'50%'}
                                >
                                    <TextInput
                                        error={getValidationErrorTranslation(validationErrors.get('houseNumber'))}
                                        id="houseNumber"
                                        onBlur={() => validateOne('houseNumber')}
                                        onChange={({ target: { value } }) => handleChange('houseNumber', value)}
                                        placeholder={language.common.houseNumber}
                                        value={employmentTaxesDetails.houseNumber || ''}
                                    />
                                </Box>
                                <Box width={'50%'}>
                                    <TextInput
                                        error={getValidationErrorTranslation(validationErrors.get('houseNumberSuffix'))}
                                        onBlur={() => validateOne('houseNumberSuffix')}
                                        onChange={({ target: { value } }) => handleChange('houseNumberSuffix', value)}
                                        placeholder={language.common.houseNumberSuffix}
                                        value={employmentTaxesDetails.houseNumberSuffix || ''}
                                    />
                                </Box>
                            </Box>
                        </FormGroup>

                        <FormGroup
                            hasErrors={validationErrors.has('zip')}
                            isRequired
                            label={language.common.zipcode}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(validationErrors.get('zip'))}
                                onBlur={() => validateOne('zip')}
                                onChange={({ target: { value } }) => handleChange('zip', value)}
                                value={employmentTaxesDetails.zip || ''}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={validationErrors.has('city')}
                            isRequired
                            label={language.common.city}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(validationErrors.get('city'))}
                                onBlur={() => validateOne('city')}
                                onChange={({ target: { value } }) => handleChange('city', value)}
                                value={employmentTaxesDetails.city || ''}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={validationErrors.has('country')}
                            isRequired
                            label={language.common.country}
                        >
                            <Select
                                error={getValidationErrorTranslation(validationErrors.get('country'))}
                                onBlur={() => validateOne('country')}
                                onChange={(country) => {
                                    handleChange('country', country?.value || null);

                                    const employmentTaxesDetailsToValidate = {
                                        ...employmentTaxesDetails,
                                        country: country?.value || null,
                                    };

                                    validateOne('country', employmentTaxesDetailsToValidate);
                                    if (employmentTaxesDetails.zip) {
                                        validateOne('zip', employmentTaxesDetailsToValidate);
                                    }
                                }}
                                options={getCountryOptions()}
                                placeholder={language.common.countryPlaceholder}
                                value={getCountryOption(employmentTaxesDetails.country)}
                            />
                        </FormGroup>
                    </FormSection>
                </>
            )}

            {showCopyDataToAllEmploymentsCheckbox && (
                <>
                    <Divider />
                    <Checkbox
                        checked={copyDataToAllEmployments}
                        label="Kopieer deze gegevens naar al mijn organisaties"
                        onChange={(event) => setCopyDataToAllEmployments(event.target.checked)}
                    />
                </>
            )}

            <ButtonRow
                justify="right"
                sx={{ marginBottom: '16px', marginTop: '16px' }}
            >
                <Button
                    onClick={() => props.onClose()}
                    variant="outline"
                >
                    {language.common.cancel}
                </Button>
                <Button
                    onClick={async () => {
                        await handleSubmit();
                    }}
                >
                    {language.common.send}
                </Button>
            </ButtonRow>
        </Form>
    );
    function handleChange<K extends keyof IEmploymentTaxesDetailsAddEdit>(
        key: K,
        value: IEmploymentTaxesDetailsAddEdit[K]
    ) {
        if (key === 'isNaturalPerson') {
            if (value) {
                setEmploymentTaxesDetails({
                    ...employmentTaxesDetails,
                    country: getCountryCodes().NL,
                    isNaturalPerson: true,
                });
            } else {
                setEmploymentTaxesDetails({ ...emptyEmploymentTaxesDetails, isNaturalPerson: false });
            }
            setValidationErrors(emptyValidationErrors);
        } else {
            setEmploymentTaxesDetails({ ...employmentTaxesDetails, [key]: value });
        }
    }

    function validateOne(
        key: keyof IEmploymentTaxesDetailsAddEdit,
        employmentTaxesDetailsToValidate?: IEmploymentTaxesDetailsAddEdit
    ) {
        const errors = updateValidationErrorsForOne(
            employmentTaxesDetailsAddEditSchema,
            employmentTaxesDetailsToValidate ?? employmentTaxesDetails,
            validationErrors,
            key
        );

        setValidationErrors(errors);
    }

    async function handleSubmit() {
        if (!isSaving) {
            setIsSaving(true);

            const { errors, item } = validate(employmentTaxesDetailsAddEditSchema, employmentTaxesDetails);

            if (errors.size > 0) {
                setValidationErrors(errors);
            } else {
                if (copyDataToAllEmployments || !previousAndCurrentEmploymentTaxesDetailsMatch()) {
                    await abortableFetch.fetchJson(`/api/employmentTaxesDetails/create`, 'POST', {
                        copyDataToAllEmployments,
                        employmentId: selectedEmploymentId,
                        employmentTaxesDetails: item,
                    });
                }
                setValidationErrors(emptyValidationErrors);
                props.onSubmit(copyDataToAllEmployments);
            }
            setIsSaving(false);
        }
    }

    function previousAndCurrentEmploymentTaxesDetailsMatch() {
        const previousTaxesDetails = props.employmentTaxesDetails;
        const currentTaxesDetails = employmentTaxesDetails;

        return (Object.keys(currentTaxesDetails) as Array<keyof IEmploymentTaxesDetails>).every(
            (key) => previousTaxesDetails?.[key] === currentTaxesDetails[key]
        );
    }

    async function loadAllActivePractitionerEmployments() {
        if (practitionerId) {
            const result = await abortableFetch.fetchJson(
                `/api/employments/${practitionerId}/organizations?isActive=true&isNoManager=true`,
                'GET'
            );

            if (result.json.items.length > 1) {
                setShowCopyDataToAllEmploymentsCheckbox(true);
            }
        }
    }
}
